import React, { useState } from 'react';

// Back-door survey upload. Not linked from the navbar by design — navigate to
// /upload manually. Pushes a raw survey JSON straight into a customer's
// unallocated surveys, exactly as the client apps do. The target is the
// customer you are currently switched to, unless an upload token is supplied.
export const FileUpload = () => {
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('Choose a survey JSON file');
    const [token, setToken] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null); // { ok: bool, message: string }

    const onFileInputChange = e => {
        const chosen = e.target.files[0] || null;
        setFile(chosen);
        setFileName(chosen ? chosen.name : 'Choose a survey JSON file');
    };

    const onSubmit = async e => {
        e.preventDefault();
        const form = e.target;
        if (!file) {
            setStatus({ ok: false, message: 'Please choose a JSON file first.' });
            return;
        }
        setBusy(true);
        setStatus(null);
        const formData = new FormData();
        formData.append('file', file);
        const headers = {};
        // A token routes the survey to that token's customer; otherwise the
        // server falls back to the customer this session is switched to.
        if (token.trim()) {
            headers.token = token.trim();
        }
        try {
            const res = await fetch('api/Upload', {
                method: 'POST',
                headers,
                body: formData
            });
            if (!res.ok) {
                const text = await res.text();
                setStatus({ ok: false, message: text || `Upload failed (${res.status}).` });
                return;
            }
            const data = await res.json();
            setStatus({ ok: true, message: `Uploaded "${data.fileName}" to unallocated surveys.` });
            setFile(null);
            setFileName('Choose a survey JSON file');
            form.reset();
        } catch (err) {
            setStatus({ ok: false, message: err.message || 'Upload failed.' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="container" style={{ maxWidth: 640 }}>
            <h1>Survey Upload</h1>
            <p className="text-muted">
                Uploads raw survey JSON into a customer's unallocated surveys, just as
                the client apps do. It targets the customer you are currently switched
                to, unless you enter an upload token below.
            </p>
            <form onSubmit={onSubmit}>
                <div className="mb-3">
                    <label className="form-label" htmlFor="tokenInput">
                        Upload token{' '}
                        <span className="text-muted">(optional — leave blank to use the current customer)</span>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="tokenInput"
                        value={token}
                        onChange={e => setToken(e.target.value)}
                        placeholder="Customer token"
                        autoComplete="off"
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label" htmlFor="customFile">{fileName}</label>
                    <input
                        type="file"
                        className="form-control"
                        id="customFile"
                        accept=".json,application/json"
                        onChange={onFileInputChange}
                    />
                </div>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                    {busy ? 'Uploading…' : 'Upload'}
                </button>
            </form>
            {status && (
                <div className={`alert mt-4 ${status.ok ? 'alert-success' : 'alert-danger'}`} role="alert">
                    {status.message}
                </div>
            )}
        </div>
    );
};
